import React, { useState, ReactElement, useRef } from 'react';
import { css } from 'glamor';
import styled from '@emotion/styled';
import Button from '../../atoms/buttons/Button';
import { isDarkMode } from '../../utils/helpers';
import { getTextColor, type } from '../../style';
import { FONT_FAMILIES } from 'theme/material/constants';
import breakpoints from 'theme/material/breakpoints';

const CarouselContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const CarouselStyled = styled('div')({
  position: 'relative',
});

const StyledList = styled('ul')({
  padding: 0,
  margin: 0,
  listStyleType: 'none',
});

const ButtonsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  marginTop: 20,
});

const StyledCloseButton = styled('span')(() => ({
  color: isDarkMode() ? '#CAE3F5' : '#8392A7',
  fontSize: 15,
  lineHeight: '11px',
  fontWeight: 'normal',
  textDecoration: 'none',
  marginTop: 20,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'none',
  },
}));

const ContentList = styled('ul')({
  listStyleType: 'disc',
  paddingLeft: 24,
});

const ContentListItem = styled('li')({
  textAlign: 'left',
  paddingBottom: 10,
});

const ContentParagraphItem = styled('p')({
  paddingBottom: 10,
});

const slideStyle = {
  marginRight: 'auto',
  marginLeft: 'auto',
  maxWidth: 900,
  listStyleType: 'none',
  textAlign: 'center',
  display: 'none',
  minHeight: 200,
};

const slideStyleActive = {
  display: 'block',
};

const SlideContentWrapper = styled('div')(() => ({
  fontFamily: FONT_FAMILIES.primary,
  fontSize: 14,
  lineHeight: '14px',
  letterSpacing: '-0.3px',
  ...getTextColor().DARK_2,
  maxWidth: 390,
  [breakpoints.down(639.05)]: {
    lineHeight: '19px',
  },
}));

const ArrowStyled = styled('a')({
  position: 'absolute',
  top: '5%',
  display: 'block',
  color: '#111111',
  cursor: 'pointer',
  opacity: '0.75',
  transform: 'translateY(-50%)',
  transition: 'opacity .15s cubic-bezier(.4, 0, 1, 1)',
});

const arrowLeft = {
  left: 32,
};

const arrowRight = {
  right: 32,
};

const StyledIndicators = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  marginTop: 20,
};

const StyledIndicatorsList = styled('li')({
  '&:nth-of-type(n + 2)': {
    marginLeft: 9,
  },
});

const indicatorStyle = {
  display: 'block',
  width: 8,
  height: 8,
  backgroundColor: '#FFFFFF',
  cursor: 'pointer',
  transition: 'opacity .15s cubic-bezier(.4, 0, 1, 1)',
  borderRadius: '50%',
  border: 'solid 1px #2B8FD7',
};

const indicatorStyleActive = {
  backgroundColor: '#2B8FD7',
};

const MainContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '20px 0',
});

const TitleContainer = styled('h3')(({ slideWiseTitleFontWeight }: { slideWiseTitleFontWeight: number }) => ({
  ...type.title.SM,
  fontStyle: 'normal',
  fontWeight: slideWiseTitleFontWeight ?? 700,
  lineHeight: '14px',
  letterSpacing: '-0.3px',
  ...getTextColor().DARK_4,
  margin: 0,
  [breakpoints.down(639.05)]: {
    fontWeight: 700,
    lineHeight: '23px',
  },
}));

const IconWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const SubTitle = styled('h4')(() => ({
  fontFamily: FONT_FAMILIES.secondary,
  fontSize: 20,
  fontWeight: 400,
  lineHeight: '13px',
  letterSpacing: '-0.2px',
  ...getTextColor().DARK_4,
  margin: 0,
  marginBottom: 20,
}));

const CarouselLeftArrow = onClick => {
  return (
    <ArrowStyled href="#" className={`${css(arrowLeft)}`} onClick={onClick}>
      {'<'}
    </ArrowStyled>
  );
};

const CarouselRightArrow = onClick => {
  return (
    <ArrowStyled href="#" className={`${css(arrowRight)}`} onClick={onClick}>
      {'>'}
    </ArrowStyled>
  );
};

const CarouselIndicator = (idx, activeIdx, onClick, clickable) => {
  return (
    <StyledIndicatorsList key={idx}>
      <a
        className={
          idx == activeIdx ? `${css({ ...indicatorStyle, ...indicatorStyleActive })}` : `${css(indicatorStyle)}`
        }
        style={{ cursor: clickable ? 'pointer' : 'default' }}
        onClick={clickable ? onClick : null}
      />
    </StyledIndicatorsList>
  );
};

const CarouselSlide = (idx, activeIdx, slide, isParagraphContent) => {
  const imgSrc = slide.icon;

  return (
    <li
      key={Math.floor(Math.random() * 999)}
      className={idx == activeIdx ? `${css({ ...slideStyle, ...slideStyleActive })}` : `${css(slideStyle)}`}
    >
      <MainContent>
        {imgSrc && (
          <IconWrapper>
            <div style={{ height: 100, width: 100 }}>{imgSrc}</div>
          </IconWrapper>
        )}

        <TitleContainer slideWiseTitleFontWeight={slide.titleFontWeight}>{slide.title}</TitleContainer>
        <SlideContentWrapper>
          {!isParagraphContent ? (
            <ContentList>
              {slide.content.map((item, index) => {
                return <ContentListItem key={index}>{item}</ContentListItem>;
              })}
            </ContentList>
          ) : (
            slide.content.map((item, index) => {
              return <ContentParagraphItem key={index}>{item}</ContentParagraphItem>;
            })
          )}
        </SlideContentWrapper>
      </MainContent>
      <SubTitle>{slide.subTitle}</SubTitle>
    </li>
  );
};

interface CarouselProps {
  slidesData: any;
  hideArrows: boolean;
  clickableIndicators: boolean;
  onClose: any;
  onCloseLabel?: string;
  isFirstTimeOffer: boolean;
  isParagraphContent?: boolean;
  customButtonContainerStyle?: any;
}

const Carousel = ({
  slidesData = {},
  hideArrows = true,
  clickableIndicators = false,
  onClose = null,
  isFirstTimeOffer = false,
  onCloseLabel = 'Close',
  isParagraphContent = false,
  customButtonContainerStyle = {},
}: CarouselProps): ReactElement => {
  const [activeIndex, setActiveIndex] = useState(0);
  const closeBtnRef = useRef(null);

  const goToSlide = (idx: number) => {
    setActiveIndex(idx);
  };

  const goToPrevSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    let idx = activeIndex;
    const slidesLength = slidesData.length;

    if (idx < 1) {
      idx = slidesLength;
    }

    --idx;
    setActiveIndex(idx);
  };

  const goToNextSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    let idx = activeIndex;
    const slidesLength = slidesData.length - 1;

    if (idx === slidesLength) {
      return false;
      // idx = -1;
    }

    ++idx;
    setActiveIndex(idx);
    closeBtnRef?.current?.blur();
  };

  const isLastSlide = activeIndex === slidesData.length - 1;

  return (
    <CarouselContainer>
      <CarouselStyled>
        {!hideArrows && <CarouselLeftArrow onClick={e => goToPrevSlide(e)} />}
        <StyledList>
          {slidesData.map((slide, index) => CarouselSlide(index, activeIndex, slide, isParagraphContent))}
        </StyledList>
        {!hideArrows && <CarouselRightArrow onClick={e => goToNextSlide(e)} />}

        <StyledList className={`${css(StyledIndicators)}`}>
          {slidesData.map((slide, index) =>
            CarouselIndicator(index, activeIndex, () => goToSlide(index), clickableIndicators)
          )}
        </StyledList>
        <ButtonsContainer style={customButtonContainerStyle}>
          <Button
            buttonRef={closeBtnRef}
            id="carouselNextBtn"
            buttonType="primaryRed"
            onClick={isLastSlide ? () => onClose(closeBtnRef) : e => goToNextSlide(e)}
            type="submit"
            data-test-id="carousel-next-btn"
            customStyle={{
              borderRadius: '4px !important',
              fontWeight: 600,
              fontSize: 14,
              lineHeight: '16px',
              letterSpacing: -0.28,
              width: 160,
              height: 'auto',
              padding: '10px 20px',
            }}
          >
            {isLastSlide ? (isFirstTimeOffer ? 'Start Making Offers' : onCloseLabel) : 'Next'}
          </Button>
          {!isLastSlide && (
            <StyledCloseButton data-test-id="carousel-close-btn" onClick={onClose}>
              Close
            </StyledCloseButton>
          )}
        </ButtonsContainer>
      </CarouselStyled>
    </CarouselContainer>
  );
};

export default Carousel;
